/**
 * Leave types settings panel (#wagepro-leave-phase2).
 * Lists the entity's active leave types (system + custom) and allows:
 *  - Editing name, is_paid, tracks_balance, accrual_method,
 *    annual_entitlement_days_scaled for any type, plus the entitlement
 *    policy for fixed-annual / monthly types: pro-rating from the join date,
 *    the carry-forward cap, and the month the leave year starts. Saving a
 *    policy applies it to every existing and future employee (backend
 *    entitlement engine) — no manual opening balances needed.
 *  - System types: code is locked (immutable per backend rules).
 *  - Archiving a type (is_archived toggle) for custom types.
 *  - Creating a new custom leave type (empty form row).
 *
 * Wired from `settings_screen.tsx` under a "Leave types" tab.
 *
 * >150 lines → business logic extracted to `leave_types_panel_utils.ts`.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from '@mukil/i18n';
import {
  Alert,
  Button,
  Checkbox,
  EmptyState,
  GlassCard,
  Input,
  SkeletonList,
  StyledSelect,
  type StyledSelectOption,
} from '@jumpstone/ui-react';
import { useLeaveTypes } from '../../data/use_leave_types';
import type { LeaveType } from '../../data/use_leave_types';
import type { LeaveTypeUpsertResult } from '../../data/use_leave_types';
import { format_month_name } from '../../lib/format';
import {
  make_empty_draft,
  accrual_method_options,
  carry_forward_options,
  draft_from_leave_type,
  draft_to_upsert_input,
  format_days_input,
  is_policy_method,
  parse_days_scaled,
  policy_summary,
  validate_leave_type_draft,
  leave_type_upsert_error_key,
} from './leave_types_panel_utils';
import type {
  CarryForwardMode,
  LeaveTypeDraft,
} from './leave_types_panel_utils';
import { LeavePolicyGroupsPanel } from './leave_policy_groups_panel';
import { LeaveNegativeBalanceCard } from './leave_negative_balance_card';

/** Trigger shell for a `StyledSelect` (the prop replaces default classes). */
const SELECT_TRIGGER_CLASSES =
  'nv-input flex h-9 w-full cursor-pointer items-center justify-between gap-2';

interface EditRowProps {
  draft: LeaveTypeDraft;
  is_system: boolean;
  accrual_options: StyledSelectOption[];
  carry_options: StyledSelectOption[];
  month_options: StyledSelectOption[];
  on_change: (patch: Partial<LeaveTypeDraft>) => void;
  on_save: () => void;
  on_cancel: () => void;
  is_saving: boolean;
  field_errors: Partial<Record<keyof LeaveTypeDraft, string>>;
}

function EditRow({
  draft,
  is_system,
  accrual_options,
  carry_options,
  month_options,
  on_change,
  on_save,
  on_cancel,
  is_saving,
  field_errors,
}: EditRowProps): React.JSX.Element {
  const { t } = useTranslation('wp');
  return (
    <div className="glass-soft rounded-xl p-4 flex flex-col gap-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="flex flex-col gap-1">
          <span className="nv-label">
            {t('settings.leave_types.field_code')}
          </span>
          <Input
            type="text"
            value={draft.code}
            disabled={is_system}
            onChange={(e) => on_change({ code: e.target.value })}
          />
          {field_errors.code !== undefined && (
            <p className="nv-help is-error">{field_errors.code}</p>
          )}
        </label>
        <label className="flex flex-col gap-1">
          <span className="nv-label">
            {t('settings.leave_types.field_name')}
          </span>
          <Input
            type="text"
            value={draft.name}
            onChange={(e) => on_change({ name: e.target.value })}
          />
          {field_errors.name !== undefined && (
            <p className="nv-help is-error">{field_errors.name}</p>
          )}
        </label>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <label className="flex flex-col gap-1">
          <span className="nv-label">
            {t('settings.leave_types.field_accrual')}
          </span>
          <StyledSelect
            value={draft.accrual_method}
            options={accrual_options}
            onChange={(v) =>
              on_change({
                accrual_method: v as LeaveTypeDraft['accrual_method'],
              })
            }
          />
        </label>

        {is_policy_method(draft.accrual_method) && (
          <label className="flex flex-col gap-1">
            <span className="nv-label">
              {t('settings.leave_types.field_entitlement')}
            </span>
            <Input
              type="number"
              min="0"
              step="0.5"
              value={format_days_input(draft.annual_entitlement_days_scaled)}
              onChange={(e) => {
                on_change({
                  annual_entitlement_days_scaled: parse_days_scaled(
                    e.target.value,
                  ),
                });
              }}
              placeholder="0"
            />
            {draft.accrual_method === 'monthly' &&
              draft.annual_entitlement_days_scaled !== null && (
                <p className="nv-help">
                  {t('settings.leave_types.monthly_hint', {
                    days:
                      Math.round(draft.annual_entitlement_days_scaled / 12) /
                      100,
                  })}
                </p>
              )}
            {field_errors.annual_entitlement_days_scaled !== undefined && (
              <p className="nv-help is-error">
                {field_errors.annual_entitlement_days_scaled}
              </p>
            )}
          </label>
        )}

        {is_policy_method(draft.accrual_method) && (
          <label className="flex flex-col gap-1">
            <span className="nv-label">
              {t('settings.leave_types.field_leave_year_start')}
            </span>
            <StyledSelect
              value={String(draft.leave_year_start_month)}
              options={month_options}
              ariaLabel={t('settings.leave_types.field_leave_year_start')}
              triggerClassName={SELECT_TRIGGER_CLASSES}
              onChange={(v) =>
                on_change({ leave_year_start_month: parseInt(v, 10) })
              }
            />
          </label>
        )}
      </div>

      {is_policy_method(draft.accrual_method) && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <label className="flex flex-col gap-1">
            <span className="nv-label">
              {t('settings.leave_types.field_carry_forward')}
            </span>
            <StyledSelect
              value={draft.carry_forward_mode}
              options={carry_options}
              ariaLabel={t('settings.leave_types.field_carry_forward')}
              triggerClassName={SELECT_TRIGGER_CLASSES}
              onChange={(v) =>
                on_change({ carry_forward_mode: v as CarryForwardMode })
              }
            />
          </label>
          {draft.carry_forward_mode === 'capped' && (
            <label className="flex flex-col gap-1">
              <span className="nv-label">
                {t('settings.leave_types.field_carry_forward_max')}
              </span>
              <Input
                type="number"
                min="0"
                step="0.5"
                value={format_days_input(draft.carry_forward_max_days_scaled)}
                onChange={(e) => {
                  on_change({
                    carry_forward_max_days_scaled: parse_days_scaled(
                      e.target.value,
                    ),
                  });
                }}
                placeholder="0"
              />
              {field_errors.carry_forward_max_days_scaled !== undefined && (
                <p className="nv-help is-error">
                  {field_errors.carry_forward_max_days_scaled}
                </p>
              )}
            </label>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-4">
        <label className="flex items-center gap-2 cursor-pointer">
          <Checkbox
            checked={draft.is_paid}
            onCheckedChange={(next) => on_change({ is_paid: next })}
          />
          <span className="text-[13.5px]">
            {t('settings.leave_types.field_is_paid')}
          </span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <Checkbox
            checked={draft.tracks_balance}
            onCheckedChange={(next) => on_change({ tracks_balance: next })}
          />
          <span className="text-[13.5px]">
            {t('settings.leave_types.field_tracks_balance')}
          </span>
        </label>
        {is_policy_method(draft.accrual_method) && (
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              checked={draft.prorate_on_join}
              onCheckedChange={(next) => on_change({ prorate_on_join: next })}
            />
            <span className="text-[13.5px]">
              {t('settings.leave_types.field_prorate_on_join')}
            </span>
          </label>
        )}
      </div>

      {is_policy_method(draft.accrual_method) && (
        <p className="nv-help">
          {t('settings.leave_types.policy_applies_hint')}
        </p>
      )}

      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={on_cancel}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={on_save}
          loading={is_saving}
        >
          {t('settings.leave_types.save')}
        </Button>
      </div>
    </div>
  );
}

export function LeaveTypesPanel(): React.JSX.Element {
  const { t, locale } = useTranslation('wp');
  const { leave_types, state, is_loading, retry, upsert } = useLeaveTypes();
  const accrual_options = accrual_method_options(t);
  const carry_options = carry_forward_options(t);
  // Month names come from CLDR via `Intl` (same as the fiscal-year picker).
  const month_options = useMemo(
    () =>
      Array.from({ length: 12 }, (_, index) => ({
        value: String(index + 1),
        label: format_month_name(index + 1, locale),
      })),
    [locale],
  );

  const [editing_code, set_editing_code] = useState<string | null>(null);
  const [draft, set_draft] = useState<LeaveTypeDraft | null>(null);
  const [field_errors, set_field_errors] = useState<
    Partial<Record<keyof LeaveTypeDraft, string>>
  >({});
  const [save_failure, set_save_failure] = useState<string | null>(null);
  const [is_saving, set_is_saving] = useState(false);
  const [adding, set_adding] = useState(false);
  const [new_draft, set_new_draft] =
    useState<LeaveTypeDraft>(make_empty_draft());
  const [new_field_errors, set_new_field_errors] = useState<
    Partial<Record<keyof LeaveTypeDraft, string>>
  >({});
  const [new_save_failure, set_new_save_failure] = useState<string | null>(
    null,
  );
  const [is_saving_new, set_is_saving_new] = useState(false);

  function start_edit(lt: LeaveType): void {
    set_editing_code(lt.code);
    set_draft(draft_from_leave_type(lt));
    set_field_errors({});
    set_save_failure(null);
  }

  async function handle_save(): Promise<void> {
    if (draft === null || upsert === undefined) return;
    const errors = validate_leave_type_draft(draft, t);
    if (Object.keys(errors).length > 0) {
      set_field_errors(errors);
      return;
    }
    set_is_saving(true);
    set_save_failure(null);
    const result: LeaveTypeUpsertResult = await upsert(
      draft_to_upsert_input(draft),
    );
    set_is_saving(false);
    if (result.outcome === 'failure') {
      set_save_failure(t(leave_type_upsert_error_key(result.failure)));
      return;
    }
    set_editing_code(null);
    set_draft(null);
  }

  async function handle_save_new(): Promise<void> {
    if (upsert === undefined) return;
    const errors = validate_leave_type_draft(new_draft, t);
    if (Object.keys(errors).length > 0) {
      set_new_field_errors(errors);
      return;
    }
    set_is_saving_new(true);
    set_new_save_failure(null);
    const result: LeaveTypeUpsertResult = await upsert(
      draft_to_upsert_input(new_draft),
    );
    set_is_saving_new(false);
    if (result.outcome === 'failure') {
      set_new_save_failure(t(leave_type_upsert_error_key(result.failure)));
      return;
    }
    set_adding(false);
    set_new_draft(make_empty_draft());
    set_new_field_errors({});
    set_new_save_failure(null);
  }

  if (is_loading) {
    return (
      <GlassCard className="rounded-2xl p-5">
        <span className="sr-only">{t('settings.leave_types.loading')}</span>
        <SkeletonList rows={3} />
      </GlassCard>
    );
  }

  if (state.status === 'entitlement_required') {
    return (
      <EmptyState
        title={t('leave.entitlement_required_title')}
        description={t('leave.entitlement_required_description')}
      />
    );
  }

  if (state.status === 'error') {
    return (
      <EmptyState
        title={t('settings.leave_types.error_title')}
        description={t('settings.leave_types.error_description')}
        action={
          <Button variant="secondary" onClick={retry}>
            {t('leave.retry')}
          </Button>
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-[14.5px] font-bold">
            {t('settings.leave_types.title')}
          </h2>
          <p className="text-[13px] text-[color:var(--ink-muted)] mt-0.5">
            {t('settings.leave_types.description')}
          </p>
        </div>
        {upsert !== undefined && !adding && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              set_adding(true);
              set_new_draft(make_empty_draft());
              set_new_field_errors({});
              set_new_save_failure(null);
            }}
          >
            {t('settings.leave_types.add_button')}
          </Button>
        )}
      </div>

      <LeaveNegativeBalanceCard />

      {adding && (
        <div>
          <EditRow
            draft={new_draft}
            is_system={false}
            accrual_options={accrual_options}
            carry_options={carry_options}
            month_options={month_options}
            on_change={(patch) => {
              set_new_draft((d) => ({ ...d, ...patch }));
              set_new_field_errors({});
            }}
            on_save={() => {
              void handle_save_new();
            }}
            on_cancel={() => {
              set_adding(false);
              set_new_draft(make_empty_draft());
              set_new_field_errors({});
              set_new_save_failure(null);
            }}
            is_saving={is_saving_new}
            field_errors={new_field_errors}
          />
          {new_save_failure !== null && (
            <Alert
              variant="danger"
              title={t('settings.leave_types.save_error_title')}
              className="mt-2"
            >
              {new_save_failure}
            </Alert>
          )}
        </div>
      )}

      <GlassCard className="rounded-2xl p-0 overflow-hidden">
        {leave_types.length === 0 && !adding && (
          <div className="p-5">
            <p className="text-[13px] text-[color:var(--ink-muted)]">
              {t('settings.leave_types.empty')}
            </p>
          </div>
        )}

        {leave_types.map((lt) => (
          <div
            key={lt.code}
            className="border-b border-[color:var(--line)] last:border-0"
          >
            {editing_code === lt.code && draft !== null ? (
              <div className="p-4">
                <EditRow
                  draft={draft}
                  is_system={lt.is_system}
                  accrual_options={accrual_options}
                  carry_options={carry_options}
                  month_options={month_options}
                  on_change={(patch) => {
                    set_draft((d) => (d !== null ? { ...d, ...patch } : d));
                    set_field_errors({});
                  }}
                  on_save={() => {
                    void handle_save();
                  }}
                  on_cancel={() => {
                    set_editing_code(null);
                    set_draft(null);
                    set_field_errors({});
                    set_save_failure(null);
                  }}
                  is_saving={is_saving}
                  field_errors={field_errors}
                />
                {save_failure !== null && (
                  <Alert
                    variant="danger"
                    title={t('settings.leave_types.save_error_title')}
                    className="mt-2"
                  >
                    {save_failure}
                  </Alert>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-between px-4 py-3">
                <div>
                  <span className="text-[13.5px] font-medium">{lt.name}</span>
                  {lt.is_system && (
                    <span className="ms-2 badge badge-neutral text-[11px]">
                      {t('settings.leave_types.system_badge')}
                    </span>
                  )}
                  <p className="text-[12px] text-[color:var(--ink-muted)] mt-0.5">
                    {lt.is_paid
                      ? t('settings.leave_types.paid')
                      : t('settings.leave_types.unpaid')}
                    {' · '}
                    {lt.tracks_balance
                      ? t('settings.leave_types.tracks_balance')
                      : t('settings.leave_types.no_balance')}
                    {policy_summary(lt, t) !== null && (
                      <>
                        {' · '}
                        {policy_summary(lt, t)}
                      </>
                    )}
                  </p>
                </div>
                {upsert !== undefined && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => start_edit(lt)}
                  >
                    {t('common.edit')}
                  </Button>
                )}
              </div>
            )}
          </div>
        ))}
      </GlassCard>

      <LeavePolicyGroupsPanel leave_types={leave_types} />
    </div>
  );
}
